import { useState } from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import type { StorePushLog } from '../types';
import { formatMoney } from '@/lib/utils/moneda';
import { ChevronDown, ChevronRight, Undo2, Loader2 } from 'lucide-react';

// ── Mapas de colores para badges ──

const PUSH_TYPE_CONFIG: Record<string, { label: string; className: string }> = {
  SMART_PRICING: {
    label: 'Smart Pricing',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  },
  RULE: {
    label: 'Regla',
    className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  },
  DISCOUNT: {
    label: 'Descuento',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  },
  MANUAL: {
    label: 'Manual',
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
  },
};

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  APPLIED: {
    label: 'Aplicado',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  },
  REVERTED: {
    label: 'Revertido',
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  },
  FAILED: {
    label: 'Fallido',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  },
};

interface PushHistoryTableProps {
  logs: StorePushLog[];
  onRevert: (logId: string) => Promise<void>;
  loading: boolean;
}

export function PushHistoryTable({ logs, onRevert, loading }: PushHistoryTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [revertingId, setRevertingId] = useState<string | null>(null);
  const [confirmRevertLog, setConfirmRevertLog] = useState<StorePushLog | null>(null);

  const handleRevert = async () => {
    if (!confirmRevertLog) return;
    setRevertingId(confirmRevertLog.id);
    try {
      await onRevert(confirmRevertLog.id);
    } finally {
      setRevertingId(null);
      setConfirmRevertLog(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p className="text-lg font-medium">Sin historial de push</p>
        <p className="text-sm mt-1">Los cambios de precios enviados a tiendas aparecerán aquí.</p>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Fecha</TableHead>
            <TableHead>Tienda</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead className="text-right">Productos</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => {
            const isExpanded = expandedId === log.id;
            const typeConf = PUSH_TYPE_CONFIG[log.pushType] || PUSH_TYPE_CONFIG.MANUAL;
            const statusConf = STATUS_CONFIG[log.status] || STATUS_CONFIG.FAILED;

            return (
              <TableRow key={log.id} className="group">
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setExpandedId(isExpanded ? null : log.id)}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </Button>
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm">
                  {new Date(log.appliedAt).toLocaleString('es-MX', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </TableCell>
                <TableCell className="text-sm font-medium">
                  {log.storeConnection?.name || log.storeConnectionId.slice(0, 8)}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={typeConf.className}>
                    {typeConf.label}
                  </Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">{log.productsAffected}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={statusConf.className}>
                    {statusConf.label}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {log.status === 'APPLIED' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmRevertLog(log)}
                      disabled={revertingId === log.id}
                    >
                      {revertingId === log.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Undo2 className="w-4 h-4 mr-1" />
                      )}
                      Revertir
                    </Button>
                  )}
                  {log.status === 'FAILED' && log.errorMessage && (
                    <span className="text-xs text-destructive">{log.errorMessage}</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}

          {/* Filas expandidas con detalle de cambios */}
          {logs.map((log) => {
            if (expandedId !== log.id || !log.changes?.length) return null;
            return (
              <TableRow key={`${log.id}-detail`} className="bg-muted/30">
                <TableCell colSpan={7} className="p-4">
                  <div className="text-sm font-medium mb-2">
                    Detalle de cambios ({log.changes.length} productos)
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SKU</TableHead>
                        <TableHead className="text-right">Precio anterior</TableHead>
                        <TableHead className="text-right">Precio nuevo</TableHead>
                        <TableHead>Descuento</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {log.changes.map((change, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-xs">{change.sku}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney(change.previousPrice)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney(change.newPrice)}
                          </TableCell>
                          <TableCell className="text-xs">
                            {change.discount
                              ? `${change.discount.label} (${change.discount.type} ${change.discount.value}${change.discount.type === 'PERCENTAGE' ? '%' : ''})`
                              : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Diálogo de confirmación para revertir */}
      <Dialog open={!!confirmRevertLog} onOpenChange={(o) => !o && setConfirmRevertLog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmar reversión</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de revertir este push? Se restaurarán los precios anteriores de{' '}
              <strong>{confirmRevertLog?.productsAffected}</strong> productos en{' '}
              <strong>{confirmRevertLog?.storeConnection?.name || 'la tienda'}</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRevertLog(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleRevert} disabled={!!revertingId}>
              {revertingId && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Revertir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
